import styled, { keyframes } from "styled-components";
import { breakpoints } from "../../../constants/media";

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.55; }
  70% { transform: scale(1.18); opacity: 0; }
  100% { transform: scale(1.18); opacity: 0; }
`;

const btnHeartbeat = keyframes`
  0%, 100% { transform: scale(1); }
  15% { transform: scale(1.04); }
  30% { transform: scale(1); }
  45% { transform: scale(1.03); }
  60% { transform: scale(1); }
`;

const dashFlow = keyframes`
  to { stroke-dashoffset: -24; }
`;

const Container = styled.div`
  width: 100%;
  margin: 80px 0px 0px 0px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  border-top: 1px solid var(--grisClaro);

  .fade-container {
    width: 100%;
    display: flex;
    justify-content: center;
  }

  section {
    width: 100%;
    max-width: 1270px;
    margin: 0 auto;
    padding: 64px 24px 0px 24px;
    display: flex;
    justify-content: center;
    box-sizing: border-box;
  }

  .novedad-wrapper {
    position: relative;
    width: 100%;
    padding: 48px 40px 40px;
    border-radius: 28px;
    background: linear-gradient(
      145deg,
      var(--celeste100) 0%,
      #fffaf2 45%,
      var(--blanco) 100%
    );
    border: 1px solid rgba(255, 151, 0, 0.12);
    box-shadow: 0 16px 48px rgba(119, 148, 229, 0.12),
      0 4px 16px rgba(255, 151, 0, 0.06);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .corner-ribbon {
    position: absolute;
    top: 0;
    right: 0;
    width: 240px;
    height: 240px;
    overflow: hidden;
    z-index: 10;
    pointer-events: none;

    span {
      position: absolute;
      display: block;
      width: 340px;
      height: 42px;
      padding: 0;
      top: 49px;
      left: -28px;
      transform: rotate(45deg) translate(1px, -1px);
      transform-origin: center center;
      background: linear-gradient(135deg, var(--naranja100), var(--naranja60));
      color: var(--blanco);
      font-size: 12.5px;
      text-align: center;
      letter-spacing: 0.01em;
      line-height: 42px;
      box-sizing: border-box;
      box-shadow:
        1px 1px 0 rgba(210, 120, 0, 0.5),
        1px 3px 6px rgba(0, 0, 0, 0.12),
        0 4px 12px rgba(255, 151, 0, 0.28);

      strong {
        display: inline-block;
        font-weight: 700;
        line-height: 1;
        transform: translate(18px, 4px);
      }

      &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 1px;
        background: rgba(255, 255, 255, 0.35);
      }

      &::after {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        bottom: -3px;
        height: 3px;
        background: linear-gradient(
          to bottom,
          rgba(0, 0, 0, 0.16),
          transparent
        );
      }
    }
  }

  .deco-blob {
    position: absolute;
    border-radius: 50%;
    pointer-events: none;

    &--1 {
      width: 220px;
      height: 220px;
      top: -80px;
      left: -60px;
      background: radial-gradient(
        circle,
        rgba(255, 151, 0, 0.12) 0%,
        transparent 70%
      );
    }

    &--2 {
      width: 180px;
      height: 180px;
      bottom: -40px;
      right: -40px;
      background: radial-gradient(
        circle,
        rgba(119, 148, 229, 0.15) 0%,
        transparent 70%
      );
    }
  }

  .badge-unificado {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 20px;
    border-radius: 50px;
    background: linear-gradient(135deg, var(--naranja100), var(--naranja60));
    box-shadow: 0 4px 14px rgba(255, 151, 0, 0.35);
    position: relative;
    z-index: 1;
    color: var(--blanco);
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 18px rgba(255, 151, 0, 0.45);
    }

    svg {
      font-size: 15px;
      color: var(--blanco);
      flex-shrink: 0;
    }

    p {
      margin: 0;
      font-weight: 600;
      font-size: 13px;
      color: var(--blanco);
      letter-spacing: 0.01em;

      span {
        font-weight: 700;
      }
    }
  }

  .title-container {
    width: 100%;
    margin: 28px 0 0;
    position: relative;
    z-index: 1;
    text-align: center;

    h3 {
      max-width: 680px;
      margin: 0 auto;
      font-weight: 600;
      font-size: 26px;
      line-height: 140%;
      letter-spacing: -0.015em;
      color: var(--negro);

      span {
        color: var(--naranja100);
      }
    }
  }

  .text-container {
    width: 100%;
    margin: 16px 0 0;
    position: relative;
    z-index: 1;
    text-align: center;

    p {
      max-width: 520px;
      margin: 0 auto;
      font-weight: 500;
      font-size: 15px;
      line-height: 190%;
      color: var(--gris);
    }
  }

  .areas-convergence {
    width: 100%;
    margin: 36px 0 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    z-index: 1;
  }

  .areas-grid {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
  }

  .area-card-wrapper {
    width: 100%;
    display: flex;
  }

  .arrows-convergence {
    width: 100%;
    height: 115px;
    margin: 0;
    flex-shrink: 0;
    overflow: visible;
  }

  .convergence-svg {
    width: 100%;
    height: 100%;
    display: block;
    overflow: visible;
  }

  .area-card {
    width: 100%;
    padding: 24px 18px 20px;
    border-radius: 20px;
    background: var(--blanco);
    border: 1px solid rgba(239, 239, 239, 0.8);
    box-shadow: 0 4px 20px rgba(119, 148, 229, 0.08);
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    transition: transform 0.25s ease, box-shadow 0.25s ease;

    &:hover {
      transform: translateY(-6px);
      box-shadow: 0 12px 32px rgba(255, 151, 0, 0.14);
    }

    &__icon {
      width: 52px;
      height: 52px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 14px;

      svg {
        font-size: 24px;
      }
    }

    h4 {
      margin: 0 0 8px;
      font-size: 15px;
      font-weight: 600;
      color: var(--negro);
    }

    &__description {
      margin: 0 0 14px;
      font-size: 12.5px;
      line-height: 165%;
      color: var(--gris);
      flex: 1;
    }

    &__email {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-wrap: wrap;
      gap: 5px;
      padding: 6px 12px;
      border-radius: 50px;
      background-color: #f6f8fd;
      font-size: clamp(10px, 2.6vw, 11.5px);
      font-weight: 500;
      color: var(--lila90);
      transition: background-color 0.2s, color 0.2s;
      max-width: 100%;
      box-sizing: border-box;
      line-height: 1.35;
      text-align: center;

      svg {
        font-size: 1.15em;
        flex-shrink: 0;
      }

      &-text {
        min-width: 0;
        text-align: center;
      }

      &:hover {
        background-color: var(--lila100);
        color: var(--lila80);
      }
    }

    &--cobranza .area-card__icon {
      background: rgba(255, 151, 0, 0.12);
      svg {
        color: var(--naranja100);
      }
    }

    &--emision .area-card__icon {
      background: rgba(119, 148, 229, 0.12);
      svg {
        color: var(--lila80);
      }
    }

    &--siniestro .area-card__icon {
      background: rgba(0, 31, 255, 0.06);
      svg {
        color: var(--azul80);
      }
    }

    &--comercial .area-card__icon {
      background: rgba(149, 163, 191, 0.15);
      svg {
        color: var(--lila90);
      }
    }
  }

  .arrow-path,
  .arrow-drop {
    stroke: var(--naranja100);
    stroke-dasharray: 8 5;
    animation: ${dashFlow} 2s linear infinite;
    opacity: 0.85;
  }

  .arrow-dot {
    fill: var(--naranja100);
  }

  .arrow-head {
    fill: var(--naranja100);
  }

  .whatsapp-hub {
    position: relative;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px 0 0;
    margin-top: 0;
  }

  .whatsapp-pulse {
    position: absolute;
    inset: -4px;
    border-radius: 50px;
    border: 2px solid var(--verde50);
    animation: ${pulse} 4s ease-out infinite;
    pointer-events: none;
  }

  .whatsapp-cta {
    position: relative;
    margin-top: 0;
    padding: 14px 28px;
    border-radius: 50px;
    background: linear-gradient(135deg, var(--verde50), var(--verde90));
    color: var(--blanco);
    font-size: 14px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    box-shadow: 0 6px 20px rgba(13, 178, 127, 0.3);
    animation: ${btnHeartbeat} 4.5s ease-in-out infinite;
    transition: box-shadow 0.2s ease;
    z-index: 1;

    svg {
      font-size: 18px;
      position: relative;
      z-index: 1;
    }

    &:hover {
      animation: none;
      transform: translateY(-2px);
      box-shadow: 0 8px 28px rgba(13, 178, 127, 0.45);
    }
  }

  .footer-info {
    width: 100%;
    margin-top: 36px;
    padding-top: 28px;
    border-top: 1px dashed rgba(119, 148, 229, 0.25);
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    position: relative;
    z-index: 1;
  }

  .subtitle-text {
    margin: 0;
    font-family: "DM Sans", sans-serif;
    font-weight: 500;
    font-size: 15px;
    line-height: 190%;
    color: #7794e5;
  }

  .second-text {
    margin: 6px 0 0;
    font-family: "DM Sans", sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 190%;
    color: #747474;
  }

  @media (max-width: ${breakpoints.desktop1200}) {
    section {
      max-width: 900px;
    }

    .areas-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .arrows-convergence {
      display: none;
    }

    .whatsapp-hub {
      margin-top: 24px;
    }

    .title-container h3 {
      font-size: 22px;
    }
  }

  @media (max-width: ${breakpoints.tablet850}) {
    section {
      max-width: 720px;
      padding: 48px 16px 0;
    }

    .novedad-wrapper {
      padding: 36px 24px 32px;
      border-radius: 22px;
    }

  }

  @media (max-width: ${breakpoints.mobile651}) {
    section {
      max-width: 100%;
    }

    .novedad-wrapper {
      padding: 28px 18px 24px;
      border-radius: 18px;
    }

    .title-container h3 {
      font-size: 20px;
    }

    .areas-grid {
      grid-template-columns: 1fr;
      gap: 12px;
    }

    .arrows-convergence {
      display: none;
    }

    .whatsapp-hub {
      margin-top: 20px;
    }

    .area-card {
      padding: 20px 16px;
      border-radius: 16px;

      &__icon {
        width: 48px;
        height: 48px;
        margin-bottom: 12px;

        svg {
          font-size: 22px;
        }
      }

      &__description {
        font-size: 13px;
        margin-bottom: 12px;
      }

      &__email {
        width: 100%;
        padding: 8px 12px;
      }
    }

    .arrows-wrapper {
      display: none;
    }

    .corner-ribbon {
      display: none;
    }

    .whatsapp-cta {
      font-size: 13px;
      padding: 12px 20px;
      text-align: center;
    }
  }

  @media (max-width: ${breakpoints.mobile414}) {
    .novedad-wrapper {
      border-radius: 16px;
    }

  }
`;

export default Container;
